import _ from 'lodash';
import { PatientMedication } from 'generated/api';
import { PatientData } from 'careand-redux/slices/healthRecords';
import moment from 'moment-timezone';

export const SUMMARY_SECTION_DATA = {
  information: {
    title: 'Medical Information',
    icon: 'circle-info',
  },
  allergies: {
    title: 'Allergies',
    icon: 'hand-dots',
  },
  medicalIssues: {
    title: 'Medical Issues',
    icon: 'files-medical',
  },
  medications: {
    title: 'Medications',
    icon: 'prescription-bottle',
  },
  history: {
    title: 'History',
    icon: 'clock-rotate-left',
  },
  preventativeScreenings: {
    title: 'Preventive Screenings',
    icon: 'microscope',
  },
  immunizations: {
    title: 'Immunizations',
    icon: 'vial-virus',
  },
  administered_drugs: {
    title: 'Med Administration',
    icon: 'syringe',
  },
  lifestyleFactors: {
    title: 'Lifestyle Factors',
    icon: 'smoking',
  },
  consultants: {
    title: 'Consultants',
    icon: 'user-doctor',
  },
} as const;

export type HealthSummarySection = keyof typeof SUMMARY_SECTION_DATA;

export type HealthSummaryViewModel = Record<HealthSummarySection, SummaryData>;

export type SummaryData = SummaryEntry[] | Sections<SummaryEntry[]>;

export type SummaryEntry = { title: string; subtitle?: string };
export type Sections<T> = {
  [entry: string]: T | undefined;
};

export function getHealthSummary(data: PatientData): HealthSummaryViewModel {
  return {
    immunizations: getImmunizations(data),
    administered_drugs: getAdministeredDrugs(data),
    medicalIssues: getPatientIssues(data),
    preventativeScreenings: getPreventativeScreenings(data),
    allergies: getAllergies(data),
    lifestyleFactors: getLifestyleFactors(data),
    consultants: getConsultants(data),
    history: getHistory(data),
    medications: getMedications(data),
    information: getInformation(data),
  };
}

// UTILITY

function getImmunizations(data: PatientData): HealthSummaryViewModel['immunizations'] {
  return _(data.immunizations)
    .filter((it) => !!it.value.given_date)
    .sortBy('given_date')
    .map((it) => ({ title: it.value.name, subtitle: formatDate(it.value.given_date) }))
    .value();
}

function getAdministeredDrugs(data: PatientData): HealthSummaryViewModel['administered_drugs'] {
  return _(data.administered_drugs)
    .filter((it) => !!it.value.given_date)
    .sortBy('given_date')
    .map((it) => ({ title: it.value.name, subtitle: formatDate(it.value.given_date) }))
    .value();
}

function getPatientIssues(data: PatientData): HealthSummaryViewModel['medicalIssues'] {
  return _(data.healthRecords?.medical_issues ?? [])
    .filter(wasNotRemoved)
    .sortBy('value.date')
    .groupBy((it) => it.value.issue_type ?? 'Current Issues')
    .mapValues((values) => values.map((issue) => ({ title: issue.value.name })))
    .value();
}

function getPreventativeScreenings(
  data: PatientData,
): HealthSummaryViewModel['preventativeScreenings'] {
  return _(data.healthRecords?.preventative_screenings ?? [])
    .filter(wasNotRemoved)
    .sortBy('value.date')
    .map((it) => ({ title: it.value.name, subtitle: it.value.notes }))
    .compact()
    .value();
}

function getAllergies(data: PatientData): HealthSummaryViewModel['allergies'] {
  return _(data.healthRecords?.allergies ?? [])
    .filter(wasNotRemoved)
    .sortBy('value.date')
    .map((it) => ({ title: it.value.name }))
    .compact()
    .value();
}

function getLifestyleFactors(data: PatientData): HealthSummaryViewModel['lifestyleFactors'] {
  return _(data.healthRecords?.lifestyle_factors ?? [])
    .filter((it) => wasNotRemoved(it) && !!it.value)
    .sortBy('value.date')
    .map((it) => ({ title: it.value!.name, subtitle: it.value!.details }))
    .compact()
    .value();
}

function getConsultants(data: PatientData): HealthSummaryViewModel['consultants'] {
  return _(data.healthRecords?.consultants ?? [])
    .filter((it) => wasNotRemoved(it) && !!it.value)
    .sortBy('value.date')
    .map((it) => ({ title: it.value.name, subtitle: it.value.specialty }))
    .value();
}

function getHistory(data: PatientData): HealthSummaryViewModel['history'] {
  return _(data.healthRecords?.history)
    .filter((it) => wasNotRemoved(it) && !!it.value)
    .sortBy('value.date')
    .groupBy((it) => it.value.history_type)
    .mapValues((values) =>
      values.map((it) => ({ title: it.value.name, subtitle: it.value.details })),
    )
    .value();
}

function getInformation(data: PatientData): HealthSummaryViewModel['information'] {
  return data.healthRecords?.information?.blood_type
    ? [{ title: `Blood Type: ${data.healthRecords?.information?.blood_type || 'N/A'}` }]
    : [];
}

function getMedications(data: PatientData): HealthSummaryViewModel['medications'] {
  return _(data.healthRecords?.medications ?? [])
    .filter((it) => wasNotRemoved(it) && !!it.value.name)
    .sortBy('value.date')
    .groupBy(getMedicationSection)
    .mapValues((sectionItems) => sectionItems.map((med) => ({ title: med.value.name })))
    .value();
}

interface ItemWithHistory {
  history?: { action: string }[];
}

function wasNotRemoved(item: ItemWithHistory): boolean {
  return (item.history ?? [])[0]?.action !== 'Removed';
}

function getMedicationSection(med: PatientMedication) {
  switch (med.value.status) {
    case 'Stop':
    case 'Hold':
      return 'Stopped';
    case 'Discontinue':
      return 'Discontinued';
    default:
      return 'Current';
  }
}

function formatDate(date?: number) {
  return date !== undefined ? moment.unix(date).format('yyyy-MM-DD') : '-';
}
